import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';
import VMasker from 'vanilla-masker';

export default class Phone {
  isValid$ = null;

  constructor() {
    this.valueInput = '';
    this.placeholderValue = '';
    this.id = 'phone-input';
    this.pattern = '^\\([2-9][0-9]{2}\\)\\s[0-9]{3}-[0-9]{4}$';
    this.matchingPattern = new RegExp(this.pattern);
    this.onPasteEventSubscription = null;
    this.maskPattern = '(999) 999-9999';
  }

  /**
   * Sets the value of the phone input.
   *
   * @param {string} newValue - The new value to set. If falsy, it defaults to an empty string.
   */
  set value(newValue) {
    const node = this.getNode();
    if (!node) {
      return;
    }

    this.valueInput = '';
    
    if (newValue) {
      this.valueInput = VMasker.toPattern(newValue, this.maskPattern);
    }

    node.value = this.valueInput;
  }

  /**
   * Getter for the value property.
   * @returns {string} The current value of the `valueInput` property.
   */
  get value() {
    return this.valueInput;
  }

  /**
   * Sets the placeholder value for the component.
   * If no value is provided, it defaults to an empty string.
   *
   * @param {string} value - The placeholder text to set.
   */
  set placeholder(value) {
    this.placeholderValue = value || '';
  }

  /**
   * Gets the placeholder value.
   * @type {string}
   */
  get placeholder() {
    return this.placeholderValue;
  }

  /**
   * Binds events to the phone input field and applies a masking pattern
   * to format the input as a phone number in the format (999) 999-9999.
   * Uses the VMasker library for input masking.
   */
  bindEvents() {
    const phoneInput = this.getNode();
    VMasker(phoneInput).maskPattern(this.maskPattern);
    this.isValid$ = this.setOnValid$();
    this.onPasteEventSubscription = fromEvent(phoneInput, 'paste').subscribe(this.dispatchKeyEvent);
  }

  destroy() {
    this.onPasteEventSubscription?.unsubscribe();
  }

  /**
   * Dispatches a 'keyup' keyboard event on the current node.
   * This method triggers a synthetic `KeyboardEvent` because Vanilla Masker use only this event
   * to format the input.
   */
  dispatchKeyEvent = () => {
    const node = this.getNode();
    node.dispatchEvent(new KeyboardEvent('keyup'));
  };

  /**
   * Generates the HTML string for a phone input component.
   *
   * @returns {string} The HTML string representing the phone input component.
   */
  getHtml() {
    return `
          <div class="nhs-c-input__wrapper" data-phone-wrapper>
            <label class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Mobile phone</label>
            <input
             id="${this.id}"
              class="nhs-c-input__field nhs-u-flex--1-0-auto" 
              placeholder="${this.placeholder}" 
              maxlength="14"
              name="phone"
              autocomplete="tel"
              type="text"
              inputmode="numeric"
              aria-describedby="mask-hidden-helper-msg"
              value="${this.value}"
              required
            />
            <span data-error-label class="nhs-c-input__label--error">Please enter a valid mobile phone number</span>
          </div>
        `;
  }

  /**
   * Retrieves the DOM element associated with the current instance's ID.
   *
   * @returns {HTMLElement | null} The DOM element with the specified ID, or null if no element is found.
   */
  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * Retrieves the closest parent element with the attribute `[data-phone-wrapper]`
   * from the current node.
   *
   * @returns {Element|null} The closest parent element with the `[data-phone-wrapper]` attribute,
   * or `null` if no such element exists.
   */
  getInputWrapper() {
    return this.getNode().closest('[data-phone-wrapper]');
  }

  /**
   * getOnChangeEvent: On input change map its value to the storage property.
   */
  getOnChangeEvent$() {
    return fromEvent(this.getNode(), 'blur').pipe(map((event) => ({ phone: event.target.value })));
  }

  /**
   * Retrieves the current value of the input node.
   *
   * @returns {string} The value of the input element.
   */
  getValue() {
    return this.getNode().value;
  }

  /**
   * Resets the phone component by clearing the value of the associated DOM node
   * and the internal value property.
   */
  reset() {
    const node = this.getNode();
    node.value = '';
    this.value = '';
  }

  /**
   * Subscribes to the change event of a node, applies a debounce time, maps to a validation function,
   * and ensures distinct output values.
   *
   * @return {Observable} An observable that emits the bound validation function whenever the event changes,
   * after debouncing and ensuring distinct emissions.
   */
  setOnValid$() {
    return fromEvent(this.getNode(), 'input').pipe(
      debounceTime(650),
      map(() => this.isValid()),
      distinctUntilChanged(),
      map((isValid) => ({ phone: this.getValue(), valid: isValid }))
    );
  }

  /**
   * Sets the invalid state for a given input node by updating its ARIA attributes
   * and applying error styling to its wrapper element.
   *
   * @param {HTMLElement} node - The input element to mark as invalid.
   */
  setInvalidState(node) {
    const wrapper = this.getInputWrapper();
    node.setAttribute('aria-invalid', true);
    wrapper.classList.add('nhs-c-input__wrapper--error');
  }

  /**
   * Sets the valid state for the given input node by removing error indicators.
   *
   * @param {HTMLElement} node - The input element to update to a valid state.
   */
  setValidState(node) {
    const wrapper = this.getInputWrapper();
    node.removeAttribute('aria-invalid');
    wrapper.classList.remove('nhs-c-input__wrapper--error');
  }

  /**
   * Validates the input value of the associated node.
   *
   * @returns {boolean} Returns `false` if the input is empty, otherwise checks
   * if the input value matches a predefined regex pattern.
   */
  isValid() {
    const node = this.getNode();
    const isEmpty = node.value === '';

    if (isEmpty) {
      return false;
    }

    // Otherwise the input is not empty and need to match with the regex.
    return this.matchingPattern.test(node.value);
  }

  /**
   * Validates the current state of the component.
   * Determines if the component's state is valid and updates its visual state accordingly.
   *
   * @returns {boolean} - Returns `true` if the component is valid, otherwise `false`.
   */
  validate() {
    const node = this.getNode();
    const isValid = this.isValid();

    if (isValid) {
      this.setValidState(node);
    } else {
      this.setInvalidState(node);
    }

    return isValid;
  }
}
